// Astro mascot, rendered inline in the hero's right slot per ASTRO.md.
// 72px square. Bubble sits to Astro's LEFT, arrow points RIGHT toward him.
// Idle "bob" animation is wired through tailwind.config.ts (animate-astro-bob).

import type { CSSProperties } from "react";

export type AstroPose =
  | "waving"
  | "happy"
  | "success"
  | "phase2"
  | "phase3"
  | "configure"
  | "coach"
  | "celebrating"
  | "error"
  | "retry"
  | "landing"
  | "welcome";

export interface InlineAstroProps {
  pose?: AstroPose;
  message?: string;
  size?: number;
}

export function InlineAstro({
  pose = "happy",
  message,
  size = 72,
}: InlineAstroProps): JSX.Element {
  const src = `/assets/astro-${pose === "coach" ? "happy" : pose}.png`;
  // Note: prototype uses astro-coach.png referenced in ASTRO.md but we don't
  // have that file in the asset bundle, so fall back to happy. Same UX.
  const wrap: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    pointerEvents: "none",
    flexShrink: 0,
  };
  const bubble: CSSProperties = {
    position: "relative",
    background: "white",
    borderRadius: 12,
    padding: "7px 12px",
    fontSize: 11,
    fontWeight: 600,
    color: "#1a2a3d",
    boxShadow: "0 6px 16px -4px rgba(3,45,96,.35)",
    whiteSpace: "nowrap",
  };
  const arrow: CSSProperties = {
    position: "absolute",
    right: -4,
    top: "50%",
    marginTop: -5,
    width: 10,
    height: 10,
    background: "white",
    transform: "rotate(45deg)",
  };
  const halo: CSSProperties = {
    position: "absolute",
    inset: "10% 10%",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(255,255,255,.25) 0%, rgba(255,255,255,.08) 45%, transparent 70%)",
    filter: "blur(5px)",
  };

  return (
    <div aria-hidden="true" style={wrap}>
      {message && (
        <div style={bubble}>
          {message}
          <div style={arrow} />
        </div>
      )}
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <div style={halo} />
        <img
          src={src}
          alt=""
          className="animate-astro-bob"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            filter: "drop-shadow(0 8px 12px rgba(3,45,96,.3))",
          }}
        />
      </div>
    </div>
  );
}
